import SelectionSingleSelectGroup from '@/components/ui/SelectionSingleSelectGroup';
import DisclosureNumberFilter from '@/components/ui/DisclosureNumberFilter';
import DisclosureBaseFilter from '@/forms/_fields_v2/DisclosureBaseFilter';
import { ethnicityOptions } from '@/pages/partners/queryUtils/ethnicities';
import { PayloadStates } from '@lore/utils';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { parse, stringify } from 'query-string';
import DialogTemplate from '@/dialogs_common/DialogTemplate';
import CloseDialogButton from '@/dialogs_common/CloseDialogButton';
import TagTypes from '@/constants/TagTypes';
import SearchFilter from '@/forms/_fields_v2/SearchFilter';
import DisclosureTagFilter from '@/forms/_fields_v2/DisclosureTagFilter';
import ClearAllFilters from '@/components/ClearAllFilters';
import { getSearchUtils } from '@/utils/query-utils';
import useQuery from '@/pages/partners/useQuery';
import useFilterCount from '@/hooks/useFilterCount';
import { SolidButton } from '@/@communityshare/ui/Button';
import DisclosureFilter from '@/forms/_fields_v2/DisclosureFilter';
import _ from 'lodash';
import useNetworkFilter from './useNetworkFilter';
import SolidButtonLoader from '@/@communityshare/ui/Button/SolidButtonLoader';
import { tagNodes_tagType } from '@/utils/query-helpers';
import { genderOptions } from '@/pages/partners/queryUtils/genders'
import {CurrencyDollarIcon} from '@heroicons/react/24/outline';

export default function PartnerFiltersDialog(props) {
  const { router } = props;

  const { location, history } = router;

  const [search, setSearch] = useState(parse(location.search));
  const [querySearch, setQuerySearch] = useState(parse(location.search));

  const debounceSetQuerySearch = useCallback(_.debounce(setQuerySearch, 250), []);

  const networkFilter = useNetworkFilter(search);

  const filterFields = {
    name: 'name',
    organization: 'organization',
    searchPartner: 'searchPartner',

    expertiseTypes: 'expertiseTypes',
    ages: 'ages',
    activities: 'activities',
    experiences: 'experiences',
    guidances: 'guidances',
    groups: 'groups',
    genders: 'genders',
    ethnicities: 'ethnicities',
    joined: 'joined',
    hoursServed: 'hoursServed',
    hoursOperator: 'hoursOperator',
    contributionAmount: 'contributionAmount',
    contributionsOperator: 'contributionsOperator',
    participation: 'participation',
    participationOperator: 'participationOperator',
    students: 'students',
    studentsOperator: 'studentsOperator',
    evaluations: 'evaluations',
    evaluationsOperator: 'evaluationsOperator',
  };

  const filterKeys = _.without(_.keys(filterFields), 'hoursOperator', 'contributionsOperator',
    'participationOperator', 'studentsOperator', 'evaluationsOperator')
  const filterCount = useFilterCount(search, filterKeys);

  function onSubmit() {
    history.push({
      pathname: location.pathname,
      search: stringify({
        ...search,
        page: 1
      })
    });
    props.onClose();
  }

  function onChange(nextSearch) {
    setSearch(nextSearch);
    debounceSetQuerySearch(nextSearch);
  }

  function getTagParams(tag) {
    return {
      where: {
        filter: {  ...networkFilter, intent: 'partner', [tagNodes_tagType(tag)]: [tag?.id] },
      },
      pagination: {
        pageSize: 0,
      }
    };
  }

  const searchUtils = getSearchUtils(search, onChange);

  const result = useQuery({
    search: querySearch
  });

  return (
    <DialogTemplate className="w-screen max-w-screen-sm h-[calc(100dvh-32px)] max-h-[calc(720px)]">
      <CloseDialogButton onClick={props.onClose} />
      <div className="flex flex-col h-full">
        <div className="space-y-1 p-6 border-b border-cs-gray-200">
          <h2 className="text-cs-gray-900 text-2xl font-semibold">
            Filters
          </h2>
          <div className="text-cs-gray-500 text-sm">
            Narrow your search results.
          </div>
        </div>
        <div className="px-6 py-8 space-y-8 overflow-y-auto h-full">
          <div className="space-y-4">
            <DisclosureFilter
              label="Search"
              value={[
                searchUtils(filterFields.name).value,
                searchUtils(filterFields.organization).value,
                searchUtils(filterFields.searchPartner).value
              ]}
            >
              <SearchFilter
                label="Name"
                description="Find partners whose first or last name matches any of the words you enter."
                value={searchUtils(filterFields.name).value}
                onChange={searchUtils(filterFields.name).onChangeValue}
              />
              <SearchFilter
                label="Organization"
                description="Find partners affiliated with an organization whose name contains the phrase you type."
                value={searchUtils(filterFields.organization).value}
                onChange={searchUtils(filterFields.organization).onChangeValue}
              />
              <SearchFilter
                label="Profile"
                description="Find partners that have a specific phrase in their biography, or in the description of their skills and experience."
                value={searchUtils(filterFields.searchPartner).value}
                onChange={searchUtils(filterFields.searchPartner).onChangeValue}
              />
            </DisclosureFilter>
          </div>
          <DisclosureTagFilter
            modelName="user"
            type={TagTypes.ExpertiseType}
            label="Skills and experience"
            getParams={getTagParams}
            value={searchUtils(filterFields.expertiseTypes).value}
            onChange={searchUtils(filterFields.expertiseTypes).onChangeArray}
          />
          <DisclosureTagFilter
            modelName="user"
            type={TagTypes.Age}
            label="Age groups"
            getParams={getTagParams}
            value={searchUtils(filterFields.ages).value}
            onChange={searchUtils(filterFields.ages).onChangeArray}
          />
          <DisclosureTagFilter
            modelName="user"
            type={TagTypes.Activity}
            label="Activites"
            getParams={getTagParams}
            value={searchUtils(filterFields.activities).value}
            onChange={searchUtils(filterFields.activities).onChangeArray}
          />
          <DisclosureTagFilter
            modelName="user"
            type={TagTypes.Experience}
            label="Life experience"
            getParams={getTagParams}
            value={searchUtils(filterFields.experiences).value}
            onChange={searchUtils(filterFields.experiences).onChangeArray}
          />
          <DisclosureTagFilter
            modelName="user"
            type={TagTypes.Guidance}
            label="Professional competencies"
            getParams={getTagParams}
            value={searchUtils(filterFields.guidances).value}
            onChange={searchUtils(filterFields.guidances).onChangeArray}
          />
          <DisclosureTagFilter
            modelName="user"
            type={TagTypes.Group}
            label="Youth types"
            getParams={getTagParams}
            value={searchUtils(filterFields.groups).value}
            onChange={searchUtils(filterFields.groups).onChangeArray}
          />
          <DisclosureBaseFilter
            modelName="user"
            label="Gender"
            getParams={function getGenderParams(model) {
              return {
                where: {
                  filter: { ...networkFilter, intent: 'partner', genders:[model.data.name] }
                },
                pagination: {
                  pageSize: 0,
                }
              };
            }}
            value={searchUtils(filterFields.genders).value}
            onChange={searchUtils(filterFields.genders).onChangeArray}
            tags={{
              state: PayloadStates.RESOLVED,
              data: genderOptions.map(function(value, index) {
                return {
                  id: index + 1,
                  data: {
                    name: value
                  }
                }
              })
            }}
          />
          <DisclosureBaseFilter
            modelName="user"
            label="Ethnicity"
            getParams={function getEthnicityParams(model) {
              return {
                where: {
                  filter: { ...networkFilter, intent: 'partner', ethnicities:[model.data.name] }
                },
                pagination: {
                  pageSize: 0
                }
              };
            }}
            value={searchUtils(filterFields.ethnicities).value}
            onChange={searchUtils(filterFields.ethnicities).onChangeArray}
            tags={{
              state: PayloadStates.RESOLVED,
              data: ethnicityOptions.map(function(value, index) {
                return {
                  id: index + 1,
                  data: {
                    name: value
                  }
                }
              })
            }}
          />
          <DisclosureFilter
            label="Joined"
            value={[
              searchUtils(filterFields.joined).value
            ]}
          >
            <SelectionSingleSelectGroup
              label="Joined within the last ..."

              value={searchUtils(filterFields.joined).value}
              onChange={searchUtils(filterFields.joined).onChangeValue}
              onClear={() => setSearch(_.omit(search, 'joined'))}
              options={['1 week', '2 weeks', '1 month', '2 months', '3 months', '6 months', 'year']}
            />
          </DisclosureFilter>

          <DisclosureNumberFilter
            label="Hours served"
            operatorValue={searchUtils(filterFields.hoursOperator).value}
            onOperatorChange={searchUtils(filterFields.hoursOperator).onChangeValue}
            numberValue={searchUtils(filterFields.hoursServed).value}
            onNumberChange={searchUtils(filterFields.hoursServed).onChangeValue}
            numberLabel="This many hours"
            numberDescription={`Partners with ${searchUtils(filterFields.hoursOperator).value} this many hours served.`}
          />

          <DisclosureNumberFilter
            label="Total in-kind contributions"
            operatorValue={searchUtils(filterFields.contributionsOperator).value}
            onOperatorChange={searchUtils(filterFields.contributionsOperator).onChangeValue}
            numberValue={searchUtils(filterFields.contributionAmount).value}
            onNumberChange={searchUtils(filterFields.contributionAmount).onChangeValue}
            numberLabel="This amount of contributions"
            numberDescription={`Partners with ${searchUtils(filterFields.contributionsOperator).value} this amount of contributions.`}
            icon={CurrencyDollarIcon}
          />

          <DisclosureNumberFilter
            label="Participation in projects & events"
            operatorValue={searchUtils(filterFields.participationOperator).value}
            onOperatorChange={searchUtils(filterFields.participationOperator).onChangeValue}
            numberValue={searchUtils(filterFields.participation).value}
            onNumberChange={searchUtils(filterFields.participation).onChangeValue}
            numberLabel="Number of projects/events"
            numberDescription={`Partners with ${searchUtils(filterFields.participationOperator).value} this much participation.`}
          />

          <DisclosureNumberFilter
            label="Students served"
            operatorValue={searchUtils(filterFields.studentsOperator).value}
            onOperatorChange={searchUtils(filterFields.studentsOperator).onChangeValue}
            numberValue={searchUtils(filterFields.students).value}
            onNumberChange={searchUtils(filterFields.students).onChangeValue}
            numberLabel="Number of students served"
            numberDescription={`Partners with ${searchUtils(filterFields.studentsOperator).value} this many students served.`}
          />

          <DisclosureNumberFilter
            label="Evaluations"
            operatorValue={searchUtils(filterFields.evaluationsOperator).value}
            onOperatorChange={searchUtils(filterFields.evaluationsOperator).onChangeValue}
            numberValue={searchUtils(filterFields.evaluations).value}
            onNumberChange={searchUtils(filterFields.evaluations).onChangeValue}
            numberLabel="Number of evaluations"
            numberDescription={`Partners with ${searchUtils(filterFields.evaluationsOperator).value} this many evaluations.`}
          />

        </div>
        <div className="p-6 flex items-center justify-between border-t border-cs-gray-200">
          <ClearAllFilters
            show={true}
            className="hover:opacity-70"
            onClick={() => setSearch(_.omit(search, _.keys(filterFields)))}
            count={filterCount}
          />
          {result._isPreviousData ? (
            <SolidButton onClick={onSubmit}>
              <SolidButtonLoader />
            </SolidButton>
          ) : (
            <SolidButton onClick={onSubmit}>
              Show {filterCount > 0 ? result.meta?.totalCount : ''} results
            </SolidButton>
          )}
        </div>
      </div>
    </DialogTemplate>
  );
};

PartnerFiltersDialog.propTypes = {
  router: PropTypes.shape({
    location: PropTypes.shape({
      search: PropTypes.string,
      pathname: PropTypes.string,
    }),
    history: PropTypes.shape({
      push: PropTypes.func,
    }),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};
